import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useAuth } from '@/auth/context'
import { describeDevice, relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Modal } from '@/ui/Modal'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Active sessions — the S-D3 panel, on S-B4's endpoints.
 *
 * The server says which row is the current one (`DeviceSessionOut.current`).
 * Comparing ids client-side is not an option: the browser is never given its
 * own session id, which is the entire point of an opaque reference token.
 *
 * Signing out the current device is possible and is confirmed first, because it
 * is the one action on this screen that ends the session you are using — and
 * confirming after the fact is not available once you are signed out.
 */
export function SessionsPanel(): JSX.Element {
  const { signOut } = useAuth()
  const toast = useToast()
  const [confirming, setConfirming] = useState<string | null>(null)
  const now = new Date()

  const sessions = useQuery(
    (signal) => call(() => api.GET('/api/v1/auth/sessions', { signal })),
    [],
  )

  const revokeOne = useMutation((sessionId: string) =>
    call(() =>
      api.DELETE('/api/v1/auth/sessions/{session_id}', {
        params: { path: { session_id: sessionId } },
      }),
    ),
  )

  const revokeOthers = useMutation(() => call(() => api.DELETE('/api/v1/auth/sessions', {})))

  const rows = sessions.state.data?.sessions ?? []
  const others = rows.filter((row) => !row.current).length

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>Active sessions</h3>
        <span className="ar-card__spacer" />
        {others > 0 ? (
          <Button
            tone="ghost"
            size="sm"
            busy={revokeOthers.pending}
            busyLabel="Signing out"
            onClick={() => {
              void (async () => {
                const result = await revokeOthers.run(undefined)
                if (!result.ok) return
                sessions.refetch()
                toast.show({
                  kind: 'success',
                  title: `Signed out ${String(result.data.revoked)} other session${
                    result.data.revoked === 1 ? '' : 's'
                  }`,
                  body: 'This device is still signed in.',
                })
              })()
            }}
          >
            Sign out everywhere else
          </Button>
        ) : null}
      </div>

      <InlineError failure={revokeOne.state.failure ?? revokeOthers.state.failure} />

      {sessions.state.status === 'loading' ? (
        <div className="ar-card__body">
          <Loading label="Loading your sessions" lines={3} />
        </div>
      ) : null}

      {sessions.state.status === 'error' ? (
        <ErrorState
          failure={sessions.state.failure}
          title="Could not load your sessions"
          onRetry={sessions.refetch}
        />
      ) : null}

      {sessions.state.status === 'ready' ? (
        <div className="ar-table-wrap">
          <table className="ar-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Address</th>
                <th>Last seen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{describeDevice(row.user_agent)}</strong>
                    {row.current ? (
                      <div
                        className="ar-subtle"
                        style={{ fontSize: 'var(--ar-text-caption-size)' }}
                      >
                        This device
                      </div>
                    ) : null}
                  </td>
                  <td className="ar-muted ar-mono">{row.ip ?? '—'}</td>
                  <td className="ar-muted">{relativeTime(row.last_seen_at, now)}</td>
                  <td className="ar-num">
                    <Button
                      tone="quiet"
                      size="sm"
                      disabled={revokeOne.pending}
                      onClick={() => {
                        if (row.current) {
                          setConfirming(row.id)
                          return
                        }
                        void (async () => {
                          const result = await revokeOne.run(row.id)
                          if (!result.ok) return
                          sessions.refetch()
                          toast.show({ kind: 'success', title: 'Session ended' })
                        })()
                      }}
                    >
                      Sign out
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="ar-card__footer">
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          Sessions end on their own after a period of inactivity, and at a hard cap regardless of
          use.
        </span>
      </div>

      {confirming ? (
        <Modal
          title="Sign out this device?"
          onClose={() => {
            setConfirming(null)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setConfirming(null)
                }}
              >
                Stay signed in
              </Button>
              <Button
                tone="danger"
                onClick={() => {
                  setConfirming(null)
                  void signOut()
                }}
              >
                Sign out
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            This is the browser you are using now. Signing it out returns you to the sign-in screen;
            your other devices are unaffected.
          </p>
        </Modal>
      ) : null}
    </section>
  )
}
